import type { LocalStore } from "./storage.js";
import { uuid } from "./eventId.js";

const ANON_KEY = "aid";
const SESSION_KEY = "ses";

interface PersistedSession {
  id: string;
  startedAt: number;
  lastActivityAt: number;
  pageViewCount: number;
  eventCount: number;
}

/**
 * Session lifecycle:
 * - anonymousId is permanent, generated once and persisted forever.
 * - sessionId is rotated when the gap since lastActivityAt exceeds the
 *   inactivity timeout (default 30 min). Tab visibility "hidden" pauses
 *   the active timer but does not, by itself, end the session — a user
 *   coming back within the timeout window keeps the same session.
 *
 * Time-on-page is split into active vs inactive: we accrue active time
 * only while the tab is visible.
 */
export class SessionManager {
  private storage: LocalStore;
  private timeoutMs: number;

  private anonymousId: string;
  private session: PersistedSession;

  // Page timing
  private pageEnteredAt = 0;
  private activeAccumMs = 0;
  private inactiveAccumMs = 0;
  private lastVisibilityChange = 0;
  private isVisible = true;

  constructor(storage: LocalStore, timeoutMs: number) {
    this.storage = storage;
    this.timeoutMs = timeoutMs;

    let aid = storage.get(ANON_KEY);
    if (!aid) {
      aid = uuid();
      storage.set(ANON_KEY, aid);
    }
    this.anonymousId = aid;

    this.session = this.loadOrCreateSession();
  }

  private loadOrCreateSession(): PersistedSession {
    const now = Date.now();
    const stored = this.storage.getJson<PersistedSession>(SESSION_KEY);
    if (stored && now - stored.lastActivityAt < this.timeoutMs) {
      return stored;
    }
    const fresh: PersistedSession = {
      id: uuid(),
      startedAt: now,
      lastActivityAt: now,
      pageViewCount: 0,
      eventCount: 0,
    };
    this.storage.setJson(SESSION_KEY, fresh);
    return fresh;
  }

  /**
   * Touch the session and rotate it if the inactivity window passed. When a
   * rotation happens, we return the *previous* session id so the caller can
   * tag a session_end event with it before we mint a fresh one.
   */
  touch(): { rotated: boolean; previousSessionId?: string } {
    const now = Date.now();
    if (now - this.session.lastActivityAt >= this.timeoutMs) {
      const previousSessionId = this.session.id;
      this.session = {
        id: uuid(),
        startedAt: now,
        lastActivityAt: now,
        pageViewCount: 0,
        eventCount: 1,
      };
      this.persist();
      return { rotated: true, previousSessionId };
    }
    this.session.lastActivityAt = now;
    this.session.eventCount++;
    this.persist();
    return { rotated: false };
  }

  markPageView(): void {
    this.session.pageViewCount++;
    this.persist();
  }

  startPageTiming(): void {
    const now = Date.now();
    this.pageEnteredAt = now;
    this.activeAccumMs = 0;
    this.inactiveAccumMs = 0;
    this.lastVisibilityChange = now;
    this.isVisible = typeof document !== "undefined" ? !document.hidden : true;
  }

  /** Call from visibilitychange. Splits accumulated time into active/inactive. */
  recordVisibility(visible: boolean): void {
    const now = Date.now();
    const delta = now - this.lastVisibilityChange;
    if (this.isVisible) this.activeAccumMs += delta;
    else this.inactiveAccumMs += delta;
    this.isVisible = visible;
    this.lastVisibilityChange = now;
  }

  /** Snapshot timing without resetting (used when emitting page_leave). */
  pageTiming(): { timeOnPageMs: number; activeTimeOnPageMs: number; inactiveTimeOnPageMs: number } {
    const now = Date.now();
    const delta = now - this.lastVisibilityChange;
    const active = this.activeAccumMs + (this.isVisible ? delta : 0);
    const inactive = this.inactiveAccumMs + (this.isVisible ? 0 : delta);
    const total = this.pageEnteredAt > 0 ? now - this.pageEnteredAt : active + inactive;
    return { timeOnPageMs: total, activeTimeOnPageMs: active, inactiveTimeOnPageMs: inactive };
  }

  getAnonymousId(): string {
    return this.anonymousId;
  }

  getSessionId(): string {
    return this.session.id;
  }

  getSessionStartedAt(): number {
    return this.session.startedAt;
  }

  /** Forget identity (user logged out, or analytics.reset). */
  reset(): void {
    this.storage.remove(ANON_KEY);
    this.storage.remove(SESSION_KEY);
    this.anonymousId = uuid();
    this.storage.set(ANON_KEY, this.anonymousId);
    this.session = {
      id: uuid(),
      startedAt: Date.now(),
      lastActivityAt: Date.now(),
      pageViewCount: 0,
      eventCount: 0,
    };
    this.persist();
  }

  private persist(): void {
    this.storage.setJson(SESSION_KEY, this.session);
  }
}
